import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import DuplicateGroup from "../components/DuplicateGroup";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";

export default function Duplicates() {
  const { user } = useAuth();
  const { t } = useLang();
  const isAdmin = user?.role === "admin";
  const [scope, setScope] = useState("mine");
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const scan = useCallback(async () => {
    setResult(null);
    try {
      const { data } = await api.get("/manage/duplicates", { params: { scope } });
      setResult(data);
      setSelected(new Set(data.groups.flatMap((g) => g.photos.slice(1).map((p) => p.id))));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setResult({ scanned: 0, groups: [] });
    }
  }, [scope]);

  useEffect(() => { scan(); }, [scan]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const doDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/manage/bulk-delete", { ids: [...selected] });
      toast.success(t("dup_deleted", data.deleted));
      setConfirmOpen(false);
      await scan();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 sm:px-10 py-16" data-testid="duplicates-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">{t("tools_eyebrow")}</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 font-light tracking-tight">{t("dup_title")}</h1>
      <p className="mt-4 max-w-2xl text-zinc-400 font-light text-sm sm:text-base">{t("dup_sub")}</p>

      {isAdmin && (
        <div className="mt-8 inline-flex border border-zinc-800" data-testid="dup-scope-toggle">
          {["mine", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${scope === s ? "bg-zinc-100 text-zinc-900" : "text-zinc-400 hover:text-zinc-100"}`}
              data-testid={`dup-scope-${s}`}
            >
              {t(s === "mine" ? "dup_scope_mine" : "dup_scope_all")}
            </button>
          ))}
        </div>
      )}

      {result === null ? (
        <p className="mt-12 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500" data-testid="dup-scanning">{t("dup_scanning")}</p>
      ) : result.groups.length === 0 ? (
        <p className="mt-12 text-zinc-300 font-light" data-testid="dup-none">{t("dup_none", result.scanned)}</p>
      ) : (
        <>
          <div className="mt-12" data-testid="dup-groups">
            {result.groups.map((g) => (
              <DuplicateGroup key={g.hash} group={g} selected={selected} onToggle={toggle} showOwner={scope === "all"} />
            ))}
          </div>
          <div className="sticky bottom-0 mt-8 -mx-6 sm:-mx-10 px-6 sm:px-10 py-4 border-t border-zinc-800 bg-zinc-950/90 [backdrop-filter:blur(10px)] flex justify-end">
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={selected.size === 0}
              className="bg-sienna text-white px-6 py-3 text-sm font-light hover:bg-sienna-400 disabled:opacity-50 transition-colors"
              data-testid="dup-delete-selected-button"
            >
              {t("dup_delete_selected", selected.size)}
            </button>
          </div>
        </>
      )}

      <ConfirmDeleteDialog open={confirmOpen} onOpenChange={setConfirmOpen} count={selected.size} busy={deleting} onConfirm={doDelete} />
    </div>
  );
}
